import './StreamingBuildLog.css';

import { BitkitAlert, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import FormattedLog from '@/components/LogViewer/old/FormattedLog';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useStepLib from '@/hooks/api/useStepLib';
import { isBuildFinished, isBuildNotFinished, isBuildNotStarted, isBuildStarted } from '@/models/Build';
import { LogFormat } from '@/models/LogFormat';
import useBuildDetailsPageStore, { updateLogViewVersion } from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { groupLog, GroupOfLines } from '@/utils/buildLog';
import datadogRumCustomTiming from '@/utils/datadogRumCustomTiming';

import { hasSummaryWorkflows } from '../../types/BuildLogSummary';
import { StepLogChunkLogLevel } from '../../types/StepLogResponse';
import BuildLogEmptyState from '../BuildLogEmptyState/BuildLogEmptyState';
import useBuildLog from './hooks/useBuildLog';
import useFollowLog from './hooks/useFollowLog';
import StreamingBuildLogStep from './StreamingBuildLogStep';
import StreamingBuildLogToolbar from './StreamingBuildLogToolbar';
import SwitchToStructuredLogAlert from './SwitchToStructuredLogAlert';

export const StreamingBuildLog = () => {
  const { app, build, trackingId, isViewedAsPublicPage, logUrlStatus, logSummary } = useBuildDetailsPageStore();

  const showSwitchToStructuredViewAlert = useMemo(() => {
    return build.logFormat === LogFormat.JSON && hasSummaryWorkflows(logSummary) && logUrlStatus === 'present';
  }, [build.logFormat, logUrlStatus, logSummary]);

  const { isMobile } = useResponsive();
  const isBuildRunning = isBuildNotFinished(build);
  const [stepOpenStateCache, setStepOpenStateCache] = useState({} as { [index: string]: boolean });
  const [shouldBreakLines, setBreakLines] = useState(false);
  const [shouldFollowLog, setFollowLog] = useState(false);
  const [rumIsSent, setRumIsSent] = useState(false);
  const [loadingEntireLog, setLoadingEntireLog] = useState(false);
  const [requestStartOfLog, setRequestStartOfLog] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { value: isTransitionAlertClosed, setTrue: closeTransitionAlert } = useBoolean(false);

  const { data: stepLibData, isLoading: stepLibLoading } = useStepLib();
  const { isFirstChunkLoaded, logString, status } = useBuildLog({
    appSlug: app.slug,
    buildSlug: build.slug,
    isBuildStarted: isBuildStarted(build),
    isBuildRunning,
    publicAccess: isViewedAsPublicPage,
    requestStartOfLog,
  });
  const cancelFollowLog = useCallback(() => setFollowLog(false), []);
  useFollowLog({ ref: containerRef, anchorRef, enabled: shouldFollowLog && isBuildRunning, onScroll: cancelFollowLog });

  const groups: GroupOfLines[] = useMemo(
    () => (stepLibData && logString ? groupLog(logString, stepLibData) : []),
    [stepLibData, logString],
  );

  const onLoadEntireLogClick = () => {
    setLoadingEntireLog(true);
    setStepOpenStateCache({});
    setRequestStartOfLog(true);
  };

  useEffect(() => {
    updateLogViewVersion('legacy');
  }, []);

  useEffect(() => {
    if (logString && !rumIsSent) {
      datadogRumCustomTiming('buildDetails_v2', 'buildLogFirstChunk');
      setRumIsSent(true);
    }
  }, [logString, rumIsSent]);

  const onStepToggleClick = (stepInfo: GroupOfLines['stepInfo'], index: number, previousStepState: boolean) => {
    segmentTrack(`Expand Step Log Button Clicked`, {
      build_log_type: 'legacy',
      step_id: stepInfo?.stepId,
      step_title: stepInfo?.stepTitle || 'unknown',
      step_status: stepInfo?.stepStatus || 'unknown',
      step_version: stepInfo?.stepVersion || 'unknown',
      is_step_log_open: !previousStepState,
      page_load_id: trackingId,
      app_slug: app.slug,
      build_slug: build.slug,
    });

    setStepOpenStateCache((previousStates) => ({
      ...previousStates,
      [index]: !previousStepState,
    }));
  };

  const onBreakLinesToggle = (isEnabled: boolean) => {
    setBreakLines(isEnabled);
  };

  const onScrollButtonClick = (direction: 'up' | 'down') => {
    const header: HTMLElement | null = document.querySelector('.page-header');
    const tabs: HTMLElement | null = document.querySelector('.Tabs__button-list');

    const headerHeight = header?.offsetHeight || 0;
    const tabsTopPosition = tabs?.offsetTop || 0;

    const containerPadding = 16;
    const containerTop = containerRef.current?.offsetTop || 0;
    const containerHeight = containerRef.current?.offsetHeight || 0;

    const topTargetPosition = tabsTopPosition - headerHeight || containerTop - containerPadding;
    const top = direction === 'up' ? topTargetPosition : containerTop + containerHeight + containerPadding;
    window.scrollTo({
      top,
      left: 0,
      behavior: 'smooth',
    });
  };

  const logHeaderPadding = 24;
  const isStructuredViewAlertVisible = showSwitchToStructuredViewAlert && !isTransitionAlertClosed;

  const isWaitingToStart = isBuildRunning && isBuildNotStarted(build);
  const hasLogRequestFailed = status !== undefined;

  if (status === 404 && !isWaitingToStart) {
    return <BuildLogEmptyState state="LogsDeleted" />;
  }

  if (isWaitingToStart || (isBuildRunning && !logString && !hasLogRequestFailed)) {
    return <BuildLogEmptyState state="WaitingToBeStarted" />;
  }

  if (stepLibLoading || (isBuildRunning && !logString)) {
    return <BuildLogEmptyState state="Loading" />;
  }

  if (isBuildFinished(build) && !logString) {
    return <BuildLogEmptyState state="HasNoLogs" />;
  }

  return (
    <Box>
      <Box
        background="background/fixed-contrast"
        paddingBlockEnd={logHeaderPadding}
        borderTopRightRadius="8"
        borderTopLeftRadius="8"
      >
        {!isFirstChunkLoaded && (
          <BitkitAlert
            variant="info"
            borderBottomLeftRadius="0"
            borderBottomRightRadius="0"
            action={{
              label: 'Load entire log',
              onClick: onLoadEntireLogClick,
              state: loadingEntireLog ? 'loading' : undefined,
            }}
            messageText="This log is very long, so we only loaded its end."
          />
        )}
      </Box>
      <Box>
        {isStructuredViewAlertVisible && (
          <SwitchToStructuredLogAlert padding={logHeaderPadding} onClose={closeTransitionAlert} />
        )}
        <Box
          ref={containerRef}
          position="relative"
          /**
           * Transform creates a new stacking context that prevents the entire page from flickering,
           * while the user scrolls a really huge build log.
           */
          transform="translateZ(0)"
          css={{
            WebkitFontSmoothing: 'subpixel-antialiased',
          }}
        >
          <Box display="flex" flexDirection="column" className="buildLog__content">
            <Box
              color="text/on-color"
              className={`buildLog__innerContent${shouldBreakLines ? ' buildLog__innerContent--break-lines' : ''}`}
            >
              {groups.map((group, index) =>
                group.stepInfo ? (
                  <StreamingBuildLogStep
                    index={index}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${group.stepInfo.stepId}-${index}`}
                    group={group}
                    isOpen={
                      stepOpenStateCache[index] ||
                      (stepOpenStateCache[index] === undefined &&
                        (index === groups.length - 1 || group.stepInfo.stepStatus === 'error'))
                    }
                    onStepToggleClick={(idx, oldValue) => onStepToggleClick(group.stepInfo, idx, oldValue)}
                  />
                ) : (
                  <FormattedLog
                    // eslint-disable-next-line  react/no-array-index-key
                    key={index}
                    paddingLeft={48}
                    paddingRight={0}
                    messages={[{ level: StepLogChunkLogLevel.Normal, message: group.lines.join('\n') }]}
                  />
                ),
              )}
            </Box>
            {!isMobile && (
              <StreamingBuildLogToolbar
                isBuildRunning={isBuildRunning}
                shouldBreakLines={shouldBreakLines}
                shouldFollowLog={shouldFollowLog}
                onBreakLineToggle={onBreakLinesToggle}
                onFollowLog={setFollowLog}
                onScrollButtonClick={onScrollButtonClick}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        background="background/fixed-contrast"
        borderBottomLeftRadius="8"
        borderBottomRightRadius="8"
        paddingBlockEnd="24"
      />
      <Box ref={anchorRef} height="1px" />
    </Box>
  );
};
